import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'

function* fetchBlock(payload: { block_id: string }): SagaIterator {
  try {
    const block = yield effects.call(api.fetchBlock, payload.block_id)
    yield effects.put(actions.fetchBlockSuccess(block.data))
  } catch (error) {
    console.warn('Cannot fetch block', error)
    yield effects.put(actions.fetchBlockFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchLinks(): SagaIterator {
  try {
    const links = yield effects.call(api.fetchLinks)
    yield effects.put(actions.fetchLinksSuccess(links.data))
  } catch (error) {
    console.warn('Cannot fetch links', error)
    yield effects.put(actions.fetchLinksFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createLink(payload: { data: object }): SagaIterator {
  try {
    yield effects.call(api.createLink, payload.data)
    yield effects.put(actions.createLinkSuccess())
    yield effects.put(actions.fetchLinks())
  } catch (error) {
    console.warn('Cannot create links', error)
    yield effects.put(actions.createLinkFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* assignLinksToBlock(payload: {
  block_id: string
  block_link_id: string
  links: string[]
}): SagaIterator {
  try {
    const { block_id, block_link_id, links } = payload
    yield effects.call(api.assignLinksToBlock, block_id, block_link_id, links)
    yield effects.put(actions.assignLinksToBlockSuccess())
    yield effects.put(actions.fetchBlock(block_id))
  } catch (error) {
    console.warn('Cannot assign links to block', error)
    yield effects.put(
      actions.assignLinksToBlockFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* uploadFiles(payload: { files: File[] }): SagaIterator {
  try {
    const { files } = payload
    const newFilesIds = []
    let i = 0
    while (i < files.length) {
      const file = yield effects.call(api.uploadFile, files[i].name, files[i])
      newFilesIds.push(file.data.media_file_id)
      i++
    }
    yield effects.put(actions.uploadFilesSuccess(newFilesIds))
  } catch (error) {
    console.warn('Cannot upload files', error)
    yield effects.put(actions.uploadFilesFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* assignFilesToBlock(payload: {
  block_id: string
  block_file_id: string
  files: string[]
}): SagaIterator {
  try {
    const { block_id, block_file_id, files } = payload
    yield effects.call(api.assignFilesToBlock, block_id, block_file_id, files)
    yield effects.put(actions.assignFilesToBlockSuccess())
    yield effects.put(actions.fetchBlock(block_id))
  } catch (error) {
    console.warn('Cannot assign files to block', error)
    yield effects.put(
      actions.assignFilesToBlockFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchVideos(): SagaIterator {
  try {
    const videos = yield effects.call(api.fetchVideos)
    yield effects.put(actions.fetchVideosSuccess(videos.data))
  } catch (error) {
    console.warn('Cannot fetch videos', error)
    yield effects.put(actions.fetchVideosFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createVideo(payload: {
  caption: string
  preview_image_url: string
  url: string
}): SagaIterator {
  try {
    const { caption, preview_image_url, url } = payload
    yield effects.call(api.createVideo, caption, preview_image_url, url)
    yield effects.put(actions.createVideoSuccess())
    yield effects.put(actions.fetchVideos())
  } catch (error) {
    console.warn('Cannot create video', error)
    yield effects.put(actions.createVideoFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* assignVideosToBlock(payload: {
  block_id: string
  block_video_id: string
  videos: string[]
}): SagaIterator {
  try {
    const { block_id, block_video_id, videos } = payload
    yield effects.call(
      api.assignVideosToBlock,
      block_id,
      block_video_id,
      videos
    )
    yield effects.put(actions.assignVideosToBlockSuccess())
    yield effects.put(actions.fetchBlock(block_id))
  } catch (error) {
    console.warn('Cannot assign videos to block', error)
    yield effects.put(
      actions.assignVideosToBlockFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* assignGalleryToBlock(payload: {
  block_id: string
  block_gallery_id: string
  images: string[]
}): SagaIterator {
  try {
    const { block_id, block_gallery_id, images } = payload
    yield effects.call(
      api.assignGalleryToBlock,
      block_id,
      block_gallery_id,
      images
    )
    yield effects.put(actions.assignGalleryToBlockSuccess())
    yield effects.put(actions.fetchBlock(block_id))
  } catch (error) {
    console.warn('Cannot assign gallery to block', error)
    yield effects.put(
      actions.assignGalleryToBlockFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* watchFetchBlock(): SagaIterator {
  yield effects.takeEvery(actions.fetchBlock.toString(), fetchBlock)
}

function* watchFetchLinks(): SagaIterator {
  yield effects.takeEvery(actions.fetchLinks.toString(), fetchLinks)
}

function* watchCreateLink(): SagaIterator {
  yield effects.takeEvery(actions.createLink.toString(), createLink)
}

function* watchAssignLinksToBlock(): SagaIterator {
  yield effects.takeEvery(
    actions.assignLinksToBlock.toString(),
    assignLinksToBlock
  )
}

function* watchUploadFiles(): SagaIterator {
  yield effects.takeEvery(actions.uploadFiles.toString(), uploadFiles)
}

function* watchAssignFilesToBlock(): SagaIterator {
  yield effects.takeEvery(
    actions.assignFilesToBlock.toString(),
    assignFilesToBlock
  )
}

function* watchFetchVideos(): SagaIterator {
  yield effects.takeEvery(actions.fetchVideos.toString(), fetchVideos)
}

function* watchCreateVideo(): SagaIterator {
  yield effects.takeEvery(actions.createVideo.toString(), createVideo)
}

function* watchAssignVideosToBlock(): SagaIterator {
  yield effects.takeEvery(
    actions.assignVideosToBlock.toString(),
    assignVideosToBlock
  )
}

function* watchAssignGalleryToBlock(): SagaIterator {
  yield effects.takeEvery(
    actions.assignGalleryToBlock.toString(),
    assignGalleryToBlock
  )
}

export default function* watchAdminBlockEdit(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchBlock),
    effects.fork(watchFetchLinks),
    effects.fork(watchCreateLink),
    effects.fork(watchAssignLinksToBlock),
    effects.fork(watchUploadFiles),
    effects.fork(watchAssignFilesToBlock),
    effects.fork(watchFetchVideos),
    effects.fork(watchCreateVideo),
    effects.fork(watchAssignVideosToBlock),
    effects.fork(watchAssignGalleryToBlock),
  ])
}
